/**
 * Design Tokens — Standardized spacing, sizing, and animation values
 * Used across all components for consistency
 */

export const SPACING = {
  xs: 8,
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 28,
  xxxl: 32,
};

export const BORDER_RADIUS = {
  compact: 10,
  standard: 14,
  large: 18,
  xl: 26,
  full: 999,
};

export const SHADOWS = {
  subtle: '0 2px 10px rgba(0,0,0,0.20)',
  elevated: '0 8px 30px rgba(0,0,0,0.30)',
  hover: '0 20px 60px rgba(0,0,0,0.42)',
  modal: '0 30px 80px rgba(0,0,0,0.55)',
};

export const TRANSITIONS = {
  fast: '160ms cubic-bezier(0.16,1,0.3,1)',
  normal: '240ms cubic-bezier(0.16,1,0.3,1)',
  slow: '520ms cubic-bezier(0.16,1,0.3,1)',
  bounce: '520ms cubic-bezier(0.34,1.56,0.64,1)',
};

// Signature type: editorial serif for display, grotesque for UI/body.
export const FONT_DISPLAY = "'Fraunces', Georgia, 'Times New Roman', serif";
export const FONT_BODY = "'Hanken Grotesk', -apple-system, system-ui, sans-serif";

export const TYPOGRAPHY = {
  heading1: { fontFamily: FONT_DISPLAY, fontSize: '38px', fontWeight: 600, lineHeight: 1.08, letterSpacing: '-0.025em' },
  heading2: { fontFamily: FONT_DISPLAY, fontSize: '28px', fontWeight: 600, lineHeight: 1.15, letterSpacing: '-0.02em' },
  heading3: { fontFamily: FONT_DISPLAY, fontSize: '22px', fontWeight: 600, lineHeight: 1.22, letterSpacing: '-0.01em' },
  subheading: { fontSize: '16px', fontWeight: 700, lineHeight: 1.4 },
  body: { fontSize: '15px', fontWeight: 400, lineHeight: 1.6 },
  bodySmall: { fontSize: '13px', fontWeight: 400, lineHeight: 1.55 },
  label: { fontSize: '13px', fontWeight: 600, lineHeight: 1.4 },
  labelSmall: { fontSize: '12px', fontWeight: 600, lineHeight: 1.4 },
  caption: { fontSize: '12px', fontWeight: 400, lineHeight: 1.4 },
  captionSmall: { fontSize: '11px', fontWeight: 500, lineHeight: 1.3 },
  // Editorial eyebrow / kicker
  kicker: { fontFamily: FONT_BODY, fontSize: '11px', fontWeight: 700, lineHeight: 1.3, letterSpacing: '0.22em', textTransform: 'uppercase' },
};

export const TOUCH_TARGET = {
  mobile: 44, // pt
  web: 40,   // px

  // Semantic sizes, matching mobile/src/theme so a control named `default`
  // is the same size on both platforms. `default` is the WCAG 2.5.5 minimum
  // and is the right choice for anything a touch user operates, including on
  // web — `web: 40` stays available for dense desktop-only chrome.
  default: 44,
  compact: 36,
  large: 52,
};

export const INPUT_HEIGHT = {
  mobile: 44,
  web: 40,
};

export const BREAKPOINTS = {
  xs: 0,
  sm: 480,
  md: 768,
  lg: 1024,
  xl: 1280,
  xxl: 1600,
};

export const MAX_WIDTH = {
  container: '1280px',
  content: '760px',
  section: '1100px',
};

export const Z_INDEX = {
  dropdown: 10,
  sticky: 20,
  modal: 100,
  tooltip: 50,
  notification: 150,
};

export const RESPONSIVE_PADDING = {
  mobile: SPACING.md,      // 16px
  tablet: SPACING.lg,      // 20px
  desktop: SPACING.xl,     // 24px
};
